import { styled, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Multiselect } from '../../../components/Multiselect/Multiselect';
import { SingleSelect } from '../../../components/SingleSelect/SingleSelect';
import { ModalComponent } from '../../../components/ModalComponent/ModalComponent';
import { Typography } from '../../../components/Typography/Typography';
import { FormField } from '../../../components/FormField/FormField';
import UserInfo from '../../ProxyAssignment/components/AssignProxyModal/UserInfo';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useCreateApprovalDelegates } from '../../../queries/hooks/useCreateApprovalDelegates';
import { useUsers } from '../../../queries/hooks/useUsers';
import { ApprovalDelegatePerson } from '../../../queries/hooks/useApprovalDelegates';
import { SelectItem, UserRole } from '../../../types';
import { hasRole } from '../../../utils/roles';
import { extractErrorMessage } from '../../../utils/errorNotifier';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The deal owner whose approval is being delegated (fixed by the row the admin clicked). */
  owner: ApprovalDelegatePerson | null;
  /** Deals of this owner that still have at least one undelegated operating partner. */
  availableDeals: SelectItem[];
  /** Per-deal list of operating partners that are not yet delegated for this owner. */
  availableOpsByDeal: Record<number, SelectItem[]>;
}

/**
 * Delegate modal opened from a deal-owner row. The source owner is fixed; the admin picks one of that
 * owner's deals, the operating partners to cover, and the delegate. Creates one delegation per OP.
 */
export const DelegateModal = ({ isOpen, onClose, owner, availableDeals, availableOpsByDeal }: Props) => {
  const { colors } = useTheme();
  const { pushSuccessToast, pushErrorToast } = useToastMessage();
  const { data: users = [] } = useUsers('active');
  const createDelegates = useCreateApprovalDelegates();

  const [dealId, setDealId] = useState<number | null>(null);
  const [opIds, setOpIds] = useState<SelectItem[]>([]);
  const [toOwnerId, setToOwnerId] = useState<number | null>(null);

  // Reset selections whenever the modal (re)opens for a (possibly different) owner.
  useEffect(() => {
    if (isOpen) {
      setDealId(null);
      setOpIds([]);
      setToOwnerId(null);
    }
  }, [isOpen, owner?.id]);

  const ownerUser = useMemo(() => users.find((u) => u.id === owner?.id) ?? null, [users, owner?.id]);

  const opOptions = useMemo<SelectItem[]>(
    () => (dealId != null ? availableOpsByDeal[dealId] ?? [] : []),
    [dealId, availableOpsByDeal]
  );

  const delegateOptions = useMemo<SelectItem[]>(
    () =>
      users
        .filter((u) => u.isActive && hasRole(u, UserRole.DEAL_OWNER) && u.id !== owner?.id)
        .map((u) => ({ id: u.id, value: u.name })),
    [users, owner?.id]
  );

  const canSubmit = dealId != null && opIds.length > 0 && toOwnerId != null && !createDelegates.isLoading;

  const handleClose = () => {
    setDealId(null);
    setOpIds([]);
    setToOwnerId(null);
    onClose();
  };

  const handleSubmit = () => {
    if (owner == null || dealId == null || toOwnerId == null || opIds.length === 0) return;
    createDelegates.mutate(
      {
        dealId,
        operatingPartnerIds: opIds.map((o) => Number(o.id)),
        fromDealOwnerId: owner.id,
        toDealOwnerId: toOwnerId,
      },
      {
        onSuccess: (created) => {
          pushSuccessToast({
            message: `Created ${created.length} delegation${created.length === 1 ? '' : 's'}.`,
          });
          handleClose();
        },
        onError: (error) => pushErrorToast({ message: extractErrorMessage(error) }),
      }
    );
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={handleClose}
      title='Delegate Approval'
      actionButtonsProps={{
        onCancel: handleClose,
        onSubmit: handleSubmit,
        isSubmitDisabled: !canSubmit,
        submitText: 'Delegate',
        cancelText: 'Cancel',
      }}
      top='80px'
    >
      <Content>
        {ownerUser && <UserInfo user={ownerUser} roleText='Deal Owner' />}

        <FormField label='Deal'>
          <SingleSelect
            options={availableDeals}
            value={availableDeals.find((o) => o.id === dealId) ?? null}
            onChange={(_, value) => {
              setDealId(value ? Number(value.id) : null);
              setOpIds([]);
            }}
            fieldPlaceholder='Select deal'
          />
          {dealId == null && (
            <Typography variant='caption' color={colors.text.caption}>
              Select a deal first to choose its operating partners.
            </Typography>
          )}
        </FormField>

        <FormField label='Operating partners'>
          <Multiselect
            options={opOptions}
            value={opIds}
            onChange={(_, value) => setOpIds(value ?? [])}
            disabled={dealId == null}
            fieldPlaceholder='Select operating partners'
            optionName='Operating Partner'
          />
          <Typography variant='caption' color={colors.text.caption}>
            A separate delegation will be created for each operating partner you select.
          </Typography>
        </FormField>

        <FormField label='Delegate to'>
          <SingleSelect
            options={delegateOptions}
            value={delegateOptions.find((o) => o.id === toOwnerId) ?? null}
            onChange={(_, value) => setToOwnerId(value ? Number(value.id) : null)}
            fieldPlaceholder='Select deal owner'
          />
        </FormField>
      </Content>
    </ModalComponent>
  );
};

const Content = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
